// Query parameters that must never leave the app inside telemetry. Sentry records
// outgoing request URLs as breadcrumbs and the page URL on the event itself, so
// anything in a URL reaches error reports verbatim unless it is scrubbed here.
const SENSITIVE_PARAMS = ['verifyEmailToken', 'linkBack', 'email'];

// Auth tokens that travel as a path segment rather than a query parameter, which
// scrubUrlParams alone would not touch.
const SENSITIVE_PATH_PREFIXES = ['/password-setup/'];

const REDACTED = '[redacted]';

// Replace the segment after a token-bearing path prefix: /password-setup/abc123
// becomes /password-setup/[redacted].
const scrubUrlPath = (pathname: string): string => {
  const prefix = SENSITIVE_PATH_PREFIXES.find((candidate) => pathname.startsWith(candidate));
  if (!prefix) return pathname;

  const rest = pathname.slice(prefix.length);
  const nextSlash = rest.indexOf('/');

  return `${prefix}${REDACTED}${nextSlash === -1 ? '' : rest.slice(nextSlash)}`;
};

// Replace sensitive query-parameter values in a URL string, leaving everything else
// (path, other params) intact so the breadcrumb is still useful for debugging.
export const scrubUrlParams = (url: string): string => {
  if (!url) return url;

  try {
    // Relative request URLs are common here, so parse against a throwaway base.
    const parsed = new URL(url, 'http://scrub.local');
    let changed = false;

    SENSITIVE_PARAMS.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, REDACTED);
        changed = true;
      }
    });

    const scrubbedPath = scrubUrlPath(parsed.pathname);
    if (scrubbedPath !== parsed.pathname) {
      parsed.pathname = scrubbedPath;
      changed = true;
    }

    if (!changed) return url;

    return url.startsWith('http') ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    // A URL we can't parse is one we can't prove is safe — drop the query string.
    return url.split('?')[0];
  }
};

// Scrub the places Sentry carries a URL: breadcrumb messages, breadcrumb data, and the
// request on the event itself.
//
// Every string in `data` is scrubbed, not just `url`. Navigation breadcrumbs put the
// URLs in `from` and `to`, fetch/xhr ones use `url`, and the set differs by
// integration — so an allow-list of keys silently misses whichever field a future
// breadcrumb type happens to use. scrubUrlParams returns non-URL strings unchanged,
// so running it over everything is safe.
export const scrubSentryBreadcrumb = <T extends { message?: string; data?: Record<string, unknown> }>(
  breadcrumb: T,
): T => {
  if (typeof breadcrumb.message === 'string') breadcrumb.message = scrubUrlParams(breadcrumb.message);

  if (breadcrumb.data) {
    Object.entries(breadcrumb.data).forEach(([key, value]) => {
      if (typeof value === 'string' && breadcrumb.data) breadcrumb.data[key] = scrubUrlParams(value);
    });
  }

  return breadcrumb;
};
